/**
 * Store de configuración general de la aplicación
 * Gestiona la configuración cargada desde GET /configs
 * Incluye URLs de buckets S3 para la utilidad de imágenes
 */

import { defineStore } from 'pinia'
import { useConfigApi } from '~/composables/api/useConfigApi'

const DEFAULT_PROJECT_NAME = 'Maison D\'Or'

// Margen con el que se juzga el encaje de una oferta en su ventana planificada
const DEFAULT_GANTT_TOLERANCE_DAYS = 15

export const useConfigStore = defineStore('config', () => {
  // Estado reactivo para cada opción de configuración
  const appName = ref(DEFAULT_PROJECT_NAME)
  const s3AssetsBasePath = ref(null)
  const s3FilesBasePath = ref(null)
  const ganttToleranceDays = ref(DEFAULT_GANTT_TOLERANCE_DAYS)
  // Flag de features de IA en la config pública: activa replanificación asistida
  const aiFeaturesEnabled = ref(false)

  const loading = ref(false)
  const loaded = ref(false)

  // Promesa compartida para evitar N+1 llamadas simultáneas
  let loadPromise = null

  // Getter de compatibilidad (alias para s3AssetsBasePath)
  const assetsBaseUrl = computed(() => s3AssetsBasePath.value)
  const filesBaseUrl = computed(() => s3FilesBasePath.value)

  /**
   * Carga la configuración desde la API y guarda cada propiedad
   * Usa patrón de promesa compartida para evitar múltiples llamadas simultáneas
   * Solo carga una vez, las llamadas subsiguientes retornan la config cacheada
   */
  const loadConfig = async () => {
    // Si ya está cargado, retornar inmediatamente
    if (loaded.value) return

    // Si hay una petición en curso, retornar la misma promesa
    if (loadPromise) return loadPromise

    // Crear nueva promesa y guardarla
    loadPromise = (async () => {
      loading.value = true
      try {
        const { getConfig } = useConfigApi()
        const response = await getConfig()
        const data = response.data

        // La respuesta es un array de configs con { id, value, type, group }
        // Convertir a objeto para fácil acceso
        if (Array.isArray(data)) {
          const configMap = data.reduce((acc, item) => {
            if (item.id) {
              acc[item.id] = item.value
            }
            return acc
          }, {})

          appName.value = configMap.appName ?? DEFAULT_PROJECT_NAME
          s3AssetsBasePath.value = configMap.s3AssetsBasePath ?? null
          s3FilesBasePath.value = configMap.s3FilesBasePath ?? null
          ganttToleranceDays.value = Number(configMap.ganttToleranceDays ?? DEFAULT_GANTT_TOLERANCE_DAYS)
          aiFeaturesEnabled.value = configMap.aiFeaturesEnabled === true || configMap.aiFeaturesEnabled === 'true'
        }

        loaded.value = true
      } catch (error) {
        console.error('[ConfigStore] Error loading config:', error)
        throw error
      } finally {
        loading.value = false
        // Limpiar la promesa para permitir reintentos en caso de error
        loadPromise = null
      }
    })()

    return loadPromise
  }

  /**
   * Limpia la configuración (usado en logout)
   */
  const clearConfig = () => {
    appName.value = DEFAULT_PROJECT_NAME
    s3AssetsBasePath.value = null
    s3FilesBasePath.value = null
    ganttToleranceDays.value = DEFAULT_GANTT_TOLERANCE_DAYS
    aiFeaturesEnabled.value = false
    loaded.value = false
  }

  return {
    // Estado individual
    appName,
    s3AssetsBasePath,
    s3FilesBasePath,
    ganttToleranceDays,
    aiFeaturesEnabled,
    loading,
    loaded,
    // Getters de compatibilidad
    assetsBaseUrl,
    filesBaseUrl,
    // Actions
    loadConfig,
    clearConfig
  }
})
